"use client"

import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * W-444, W-445: A TINT ON A SATURATED BANNER IS BLACK. White text on a white
 * tint lost contrast on every fill (white/20 is 3.57:1 on the purple, 3.77 on
 * the red), so the buttons, the inset rows and the press states darken the fill
 * instead: black/15 at rest and black/20 hovered are 6.28 to 7.11:1 on all
 * three. The amber is amber-700, 5.02:1 under white text, where amber-500 read
 * 2.15. Measured by the WCAG formula; `status-banner-contrast.test.ts` holds it.
 */
const ACTION_BUTTON = "border-white/30 bg-black/15 text-white backdrop-blur-xs hover:bg-black/20"
const ICON_BUTTON = "text-white/70 hover:bg-black/15 hover:text-white"

/** A row inside a banner (an outstanding invoice): 5.74 to 5.99:1 under white text. */
const STATUS_BANNER_INSET = "bg-black/10"

/** A collapsed banner's full-width press state. */
const STATUS_BANNER_PRESS = "hover:bg-black/10 active:bg-black/15"

const colorMap = {
  amber: {
    container: "bg-amber-700 text-white",
    iconButton: ICON_BUTTON,
    actionButton: ACTION_BUTTON,
  },
  purple: {
    container: "bg-[#6252fd] text-white",
    iconButton: ICON_BUTTON,
    actionButton: ACTION_BUTTON,
  },
  red: {
    container: "bg-red-600 text-white",
    iconButton: ICON_BUTTON,
    actionButton: ACTION_BUTTON,
  },
} as const

type StatusBannerColor = keyof typeof colorMap

interface StatusBannerProps extends React.HTMLAttributes<HTMLDivElement> {
  color?: StatusBannerColor
}

function StatusBanner({
  color = "purple",
  className,
  children,
  ...props
}: StatusBannerProps) {
  return (
    <div
      className={cn(
        "relative z-50 mx-4 mt-3 overflow-hidden rounded-lg shadow-xs",
        colorMap[color].container,
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

function StatusBannerRow({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 px-4 py-3",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

function StatusBannerContent({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("min-w-0 flex-1", className)} {...props}>
      {children}
    </div>
  )
}

function StatusBannerActions({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex shrink-0 items-center gap-2", className)}
      {...props}
    >
      {children}
    </div>
  )
}

interface StatusBannerIconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  color?: StatusBannerColor
}

function StatusBannerIconButton({
  color = "purple",
  className,
  children,
  ...props
}: StatusBannerIconButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        "rounded-md p-1 transition-colors",
        colorMap[color].iconButton,
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
}

export {
  STATUS_BANNER_INSET,
  STATUS_BANNER_PRESS,
  StatusBanner,
  StatusBannerRow,
  StatusBannerContent,
  StatusBannerActions,
  StatusBannerIconButton,
  colorMap as statusBannerColorMap,
  type StatusBannerColor,
}
